import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function SettingsPage() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data || {})).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/settings", form);
      toast.success("Pengaturan disimpan.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Pengaturan</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Identitas Desa</h1>
        <p className="text-slate-600 text-sm mt-2">Pengaturan ini ditampilkan pada halaman publik.</p>
      </header>

      <form onSubmit={save} className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
        <Field label="Nama Desa"><Input value={form.nama_desa || ""} onChange={(e) => update("nama_desa", e.target.value)} className="h-11" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kecamatan"><Input value={form.kecamatan || ""} onChange={(e) => update("kecamatan", e.target.value)} className="h-11" /></Field>
          <Field label="Kabupaten/Kota"><Input value={form.kabupaten || ""} onChange={(e) => update("kabupaten", e.target.value)} className="h-11" /></Field>
        </div>
        <Field label="Provinsi"><Input value={form.provinsi || ""} onChange={(e) => update("provinsi", e.target.value)} className="h-11" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tahun Pilkades"><Input type="number" value={form.tahun_pilkades || ""} onChange={(e) => update("tahun_pilkades", Number(e.target.value))} className="h-11" /></Field>
          <Field label="Tanggal Pemilihan"><Input type="date" value={form.tanggal_pemilihan || ""} onChange={(e) => update("tanggal_pemilihan", e.target.value)} className="h-11" /></Field>
        </div>

        <div className="border-t border-slate-200 pt-4 mt-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Tampilan Halaman Publik</div>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition" data-testid="show-login-button-toggle">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-slate-900"
              checked={!!form.show_public_login_button}
              onChange={(e) => update("show_public_login_button", e.target.checked)}
            />
            <div>
              <div className="text-sm font-semibold">Tampilkan tombol "Masuk Petugas" di halaman publik</div>
              <div className="text-xs text-slate-500 mt-1">
                Bila <b>aktif</b>, pengunjung halaman publik melihat tombol login di header.
                Bila <b>nonaktif</b>, akses login hanya via URL langsung <code className="font-mono text-[10px] bg-slate-100 px-1 py-0.5 rounded">/login</code>.
                Untuk keamanan, disarankan <b>nonaktifkan</b> pada deployment publik.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition mt-3" data-testid="show-tps-public-toggle">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-slate-900"
              checked={!!form.show_tps_in_public_search}
              onChange={(e) => update("show_tps_in_public_search", e.target.checked)}
            />
            <div>
              <div className="text-sm font-semibold">Tampilkan TPS &amp; Lokasi pada hasil pencarian publik</div>
              <div className="text-xs text-slate-500 mt-1">
                Selama tahap <b>DPS</b> data TPS masih sementara — biasanya <b>nonaktifkan</b> agar pemilih tidak salah informasi.
                Aktifkan saat <b>DPT</b> sudah ditetapkan agar pemilih dapat melihat <b>TPS dan alamat lokasi</b> tempat memilih.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition mt-3" data-testid="include-pindah-toggle">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-slate-900"
              checked={!!form.include_pindah_domisili_in_search}
              onChange={(e) => update("include_pindah_domisili_in_search", e.target.checked)}
            />
            <div>
              <div className="text-sm font-semibold">Ikutkan Pemilih Pindah Domisili di pencarian publik</div>
              <div className="text-xs text-slate-500 mt-1">
                Aktifkan bila pemilih dengan status <b>Pindah Domisili</b> masih memiliki hak pilih dan perlu ditampilkan di pencarian NIK publik.
                Efek di BA: mereka <b>dikeluarkan dari kategori TMS</b> dan muncul di Section IV <b>"Pemilih Tidak Ada di Lokasi Alamat"</b> — tetap dihitung sebagai pemilih aktif.
                Di export Excel: pindah domisili akan masuk sheet <b>"Data Pemilih Aktif"</b> dengan keterangan "Tidak ada di Lokasi Alamat".
              </div>
            </div>
          </label>

          <div className="mt-3">
            <Field label='URL "Lihat Semua" Berita'>
              <Input
                value={form.news_view_all_url || ""}
                onChange={(e) => update("news_view_all_url", e.target.value)}
                placeholder="https://contoh.com/berita"
                className="h-11"
                data-testid="news-view-all-url-input"
              />
            </Field>
            <p className="text-xs text-slate-500 mt-1">
              Link tujuan tombol <b>&quot;Lihat Semua&quot;</b> di section &quot;Info Terbaru&quot; pada halaman publik.
              Kosongkan &rarr; tombol otomatis mengarah ke halaman <b>/berita</b> internal (menampilkan semua berita).
              Isi URL &rarr; tombol buka website eksternal di tab baru.
            </p>
          </div>
        </div>

        <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="settings-save-button">
          <Save className="h-4 w-4 mr-2" /> {loading ? "Menyimpan..." : "Simpan"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}
